import './TicketShow.scss';

import { BellFilled } from '@ant-design/icons';
import { RefreshButton, Show } from '@refinedev/antd';
import { useOne, useShow } from '@refinedev/core';
import { Flex, Tabs, Tag } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PopupMenu from '@/components/general/PopupMenu';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import TicketLogisticStateTag from '@/components/ticket/TicketLogisticStateTag';
import TicketStateTag from '@/components/ticket/TicketStateTag';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { Ticket } from '@/model/Ticket/Ticket';
import { TICKET_LOGISTIC_STATE } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';

import TicketAITimeline from './TicketAITimeline/TicketAITimeline';
import TicketShowData from './TicketShow/TicketShowDetail';
import TicketShowLogistic from './TicketShow/TicketShowLogistic';
import TicketShowServiceOrders from './TicketShow/TicketShowServiceOrders';

export const TicketShow = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const hasMenuActions = useMemo(() => hasPermission('delete'), [hasPermission]);
  const {
    result,
    query: { isLoading },
  } = useShow<Ticket>();
  const {
    result: subcategoryResult,
    query: { isFetching: subcategoryIsLoading },
  } = useOne({
    resource: 'subcategoria_ticket',
    id: result?.subcategoria || '',
    queryOptions: {
      enabled: !!result,
    },
  });
  const {
    result: empresaResult,
    query: { isFetching: empresaIsLoading },
  } = useOne({
    resource: 'empresas',
    id: result?.empresa || '',
    queryOptions: {
      enabled: !!result && !!result.empresa,
    },
  });
  const {
    result: machineResult,
    query: { isFetching: machineIsLoading },
  } = useOne({
    resource: 'moviles',
    id: result?.movil || '',
    queryOptions: {
      enabled: !!result && !!result.movil,
    },
  });
  const {
    result: referenceResult,
    query: { isFetching: referenceIsLoading },
  } = useOne({
    resource: RESOURCES.TICKETS,
    id: result?.referencia || '',
    queryOptions: {
      enabled: !!result && !!result.referencia,
    },
  });
  const {
    result: userResult,
    query: { isFetching: usersIsLoading },
  } = useOne({
    resource: 'users',
    id: result?.assigned_to || '',
    queryOptions: {
      enabled: !!result && !!result.assigned_to,
    },
  });
  const {
    result: substatusResult,
    query: { isFetching: substatusIsLoading },
  } = useOne({
    resource: 'substatus_ticket',
    id: result?.substatus || '',
    queryOptions: {
      enabled: !!result && !!result.substatus,
    },
  });
  const loadingData = useMemo(
    () =>
      isLoading ||
      empresaIsLoading ||
      subcategoryIsLoading ||
      machineIsLoading ||
      referenceIsLoading ||
      usersIsLoading ||
      substatusIsLoading,
    [
      empresaIsLoading,
      isLoading,
      machineIsLoading,
      referenceIsLoading,
      subcategoryIsLoading,
      substatusIsLoading,
      usersIsLoading,
    ],
  );

  return (
    <Show
      isLoading={loadingData}
      headerButtons={({ editButtonProps, deleteButtonProps, refreshButtonProps }) => (
        <>
          <RefreshButton {...refreshButtonProps} hideText />
          <ProtectedEditButton {...editButtonProps} />
          {hasMenuActions && (
            <PopupMenu
              menuContent={
                <ProtectedDeleteButton
                  {...deleteButtonProps}
                  type="text"
                  disabled={result?.has_receptions || result?.has_shipments}
                  tooltip={
                    result?.has_receptions || result?.has_shipments
                      ? t('No se puede eliminar el ticket porque tiene logística asociada')
                      : undefined
                  }
                />
              }
            />
          )}
        </>
      )}>
      <div className="TicketShow">
        {/* IDs y fechas */}
        {result && (
          <div className="TicketShowHeader">
            <div className="TicketShowData">
              <div className="TicketShowDataId">
                <b>{'Ticket id'}: </b>
                {result?.id ?? ''}
              </div>
              <Flex
                align="center"
                className="TicketShowRowBooleans"
                gap={8}
                style={{ marginTop: 6 }}>
                <b>{t('Estado')}: </b>
                <TicketStateTag ticketState={result.status} />
                {result?.on_hold && <Tag color="default">{t('En pausa')}</Tag>}
                {result?.no_tracking && <Tag color="red">{t('Sin seguimiento')}</Tag>}
              </Flex>
              {result.logistics_state != undefined &&
                result.logistics_state !== TICKET_LOGISTIC_STATE.SIN_GESTION_LOGISTICA && (
                  <Flex
                    align="center"
                    className="TicketShowRowBooleans"
                    gap={8}
                    style={{ marginTop: 6 }}>
                    <b>{t('Estado de logística')}: </b>
                    <TicketLogisticStateTag ticketLogisticState={result.logistics_state} />
                  </Flex>
                )}
            </div>
            <div className="TicketShowData">
              <div>
                <b>{t('Fecha creación')}: </b>
                {dayjs(result?.created).format('L HH:mm')}
              </div>
              <div>
                <b>{t('Fecha última modificación')}: </b>
                {dayjs(result?.modified).format('L HH:mm')}
              </div>
              {result?.reminder_date && (
                <div className="TicketShowDataReminder">
                  <b>{t('Próximo recordatorio')}: </b>
                  {dayjs(result?.reminder_date).format('L')}
                  <BellFilled className="TicketShowReminderBellIcon" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            label: t('Detalles'),
            key: '1',
            children: (
              <TicketShowData
                record={result}
                subcategory={subcategoryResult}
                empresa={empresaResult}
                machine={machineResult}
                reference={referenceResult}
                user={userResult}
                substatus={substatusResult}
              />
            ),
          },
          { label: t('Logística'), key: '2', children: <TicketShowLogistic record={result} /> },
          {
            label: t('Ordenes de servicio'),
            key: '3',
            children: <TicketShowServiceOrders record={result} />,
          },
          // Agregamos la tab extra solo si el ticket tiene timeline de IA.
          ...(result?.ia_timeline && result.ia_timeline.length > 0
            ? [
                {
                  label: t('Resumen de IA'),
                  key: '4',
                  children: <TicketAITimeline record={result} />,
                },
              ]
            : []),
        ]}
      />
    </Show>
  );
};
